import socket from "socket.io-client/dist/socket.io.slim";
import _ from 'lodash';
import uuid from 'uuid/v4';

/**
 * Session KEY
 * @type {string}
 */
const SESSION_KEY = '__rns:client_id';
const SERVICE_BASE_URL = '/rns/socket.io';

//  EVENTS LIST
const EVENTS = {

    //  SERVER NEW CONNECTION
    CONNECTION: 'connection',

    //  CLIENT CONNECTED TO SERVER
    CONNECT: 'connect',

    //  CLIENT/SERVER DISCONNECT
    DISCONNECT: 'disconnect',

    //  JOIN CHANNEL
    JOIN_CHANNEL: 'bind-channel',

    //  REMOVE CHANNEL
    REMOVE_CHANNEL: "unbind-channel",

    //  AUTHENTICATE CLIENT
    AUTHENTICATE: 'authenticate',

    //  BROADCAST MESSAGE
    BROADCAST: 'broadcast'
};

/**
 * Gets a unique key for the current browser session
 * @return {string}
 */
const getSessionClientId = () => {


    let clientId = window.sessionStorage.getItem(SESSION_KEY);
    if(_.isNil(clientId)){
        clientId = uuid();
        window.sessionStorage.setItem(SESSION_KEY,clientId);
    }

    return clientId;
};

/**
 * Resets session id
 */
const resetSessionId = () => {
    window.sessionStorage.removeItem(SESSION_KEY);
};


/**
 * Create realtime socket client and invokes connect
 * @param {Object} opts Options
 * @param {String} opts.appId Application ID
 * @param {String} opts.authToken Client authentication token. Validated by server to grant access to channel joining channels
 * @param {String} opts.clientId A unique client id
 * @param {Object} opts.connectOptions Connection options
 * @param {String} uri Server address
 * @param {Function} connectCallback Connection callback
 * @return {RealtimeSocketClient}
 */
export const createClient =  function (uri,opts,connectCallback){

    opts = opts || {};
    const client = new RealtimeSocketClient({
        uri: uri,
        ...opts
    });

    //  connect client
    client.connect({
        debug: process.env.NODE_ENV !== 'production',
        path: opts.path || SERVICE_BASE_URL,
        ...opts.connectOptions
    },() => {
        connectCallback(client);
    });

    return client;
};

/**
 * Event subscription
 */
class EventSubscription {

    /**
     * Event subscription info
     * @param {Object} client
     * @param {Array|String} channels
     * @param {Array|String} event
     * @param {Function} callback
     */
    constructor(client , channels , event , callback){
        this.client = client;
        this.channel = Array.isArray(channels) ? channels : [channels] ;
        this.event =  Array.isArray(event) ? event : [event];
        this.callback = callback;
    }

    /**
     * Checks whether subscription can handle event
     * @param {String} event
     * @param {String|Array} channels
     */
    _canHandleEvent(event,channels){

        let canHandle = false;
        if(Array.isArray(channels)){

            for(let i = 0 , len = channels.length;  i < len ; i++){
                if(this.channel.includes(channels[i])){
                    canHandle = true;
                    break;
                }
            }
        }
        else{
            canHandle = this.channel.includes(channels);
        }

        return canHandle && this.event.includes(event);
    }

    /**
     * Invoke channel callback
     */
    _invokeHandler(data,event,channels){

        if(this.callback){
            this.callback(data, { event , channels })
        }

    }

    /**
     * Unsubscribe from channel and event
     * @return {*}
     */
    unsubscribe(){
        return this.client.removeChannelSubscription(this);
    }

    /**
     * Same as unsubscribe
     * @return {*}
     */
    close(){
        return this.unsubscribe();
    }

}

/**
 * Realtime socket client implementation
 */
class RealtimeSocketClient {

    /**
     *  Initializes the realtime socket client with options
     * @param opts
     * @param opts.uri Server address
     * @param opts.appId Application ID
     * @param opts.authToken Client authentication token. Validated by server to grant access to channel joining channels
     * @param opts.clientId A unique client id
     * @param opts.debug Debugging
     */
    constructor(opts){

        opts = opts || {};

        if(_.isNil(opts.uri)){
            console.error('Server address is required for connection');
        }

        if(_.isNil(opts.appId)){
            console.error('Application ID is required. Please make sure a valid application id is passed to client');
        }

        if(_.isNil(opts.clientId)){
            opts.clientId = getSessionClientId();
        }

        //  active subscriptions
        this._subscriptions = [];
        this._channelSubsriptions = [];
        this._connectionOptions = opts;
    }

    /**
     * Returns id for client
     * @return {string}
     */
    getConnectionId(){
        return this._connectionOptions.clientId;
    }

    /**
     * Connects to socket io server
     * @param {Object} opts Socket IO connection options
     * @param {Function} callback Connection callback
     * @return {Object}
     */
    connect(opts,callback){

        if(this.connection){
            return this.connection;
        }

        const me = this;
        const _opts = this._connectionOptions;
        const connection = socket(_opts.uri,opts);

        connection.on(EVENTS.CONNECT,() => {

            //  clear all subscriptions
            me._subscriptions = [];
            me._channelSubsriptions = [];

            connection.emit(EVENTS.AUTHENTICATE , { applicationId: _opts.appId , token: _opts.authToken || null , channels: [] , clientId:  _opts.clientId  }, (payload) => {

                if(payload && payload['authenticated'] === true){

                    //  invoke callback to register channels subscription
                    if(!_.isNil(callback))
                        callback();

                }

            });


        });

        //  on event received from server
        connection.on(EVENTS.BROADCAST, payload => {

            me._subscriptions.forEach(s => {

                if(s._canHandleEvent(payload.event,payload.channels)){
                    s._invokeHandler(payload.data,payload.event,payload.channels);
                }

            });


        });

        //  keep connection reference
        this.connection = connection;

        //  return connection object
        return connection;
    }

    /**
     * Returns socket io connection object
     */
    getConnection(){
        return this.connection;
    }

    /**
     * Listen for event on channel
     * @param {String|Array<String>} channel Source channel
     * @param {String|Array<String>} event Event to subscribe to
     * @param {Function} callback
     */
    subscribe(channel,event,callback){

        const connection = this.connection;
        channel = Array.isArray(channel) ? _.uniq(channel) : channel;
        event = Array.isArray(event) ? _.uniq(event) : event;

        if(_.isNil(connection)){
            console.error('Connection required for operation. Invoke connect() before attempting to subscribe/unsubscribe to channels');
            throw new Error('Connection required for operation');
        }

        const subscribeNewChannel = (chn) => {
            connection.emit(EVENTS.JOIN_CHANNEL, { channels: chn });
            this._channelSubsriptions = this._channelSubsriptions.concat(chn);
        };

        if(Array.isArray(channel)){

            let _newChannels = [];
            for(let i = 0 , len = channel.length ; i < len ; i++){

                if(!this._channelSubsriptions.includes(channel[i])){
                    _newChannels.push(channel[i]);
                }

            }

            if(_newChannels.length > 0){
                subscribeNewChannel(_newChannels);
            }

        }
        else if(_.isString(channel)){

            if(!this._channelSubsriptions.includes(channel)){
                subscribeNewChannel([channel]);
            }

        }

        //  create subscription
        let _sub = new EventSubscription(this,channel,event,callback);

        //  append subscription
        this._subscriptions.push(_sub);


        return _sub;

    }

    /**
     * Gets active channel subscriptions
     * @return {Array}
     */
    getSubscriptions(){
        return this._subscriptions;
    }

    /**
     * Remove channel subscriptions
     * @param {EventSubscription} subscription The event subscription instance
     */
    removeChannelSubscription(subscription){

        if(_.isNil(this.connection)){
            console.error('Connection required for operation. Invoke connect() before attempting to subscribe/unsubscribe to channels');
            throw new Error('Connection required for operation');
        }

        if(this._subscriptions.includes(subscription)){

            //  remove subscription
            this._subscriptions.splice(this._subscriptions.indexOf(subscription));

            const isChannelOut = (channel) => {
                return this._subscriptions.filter(x => ( Array.isArray(x.channel) ? x.channel.includes(channel) : x.channel === channel )).length === 0;
            };

            if(Array.isArray(subscription.channel)){

                let _usch = [];
                subscription.channel.forEach(s => {

                    if(isChannelOut(s))
                        _usch.push(s);

                });

                if(_usch.length > 0){
                    this.connection.emit(EVENTS.REMOVE_CHANNEL,{ channels: _usch });
                    this._channelSubsriptions = this._channelSubsriptions.filter(x => !_usch.includes(x));
                }

            }
            else if(_.isString(subscription.channel)){

                if(isChannelOut(subscription.channel) && this.connection){
                    this.connection.emit(EVENTS.REMOVE_CHANNEL,{ channels: [subscription.channel] });
                    this._channelSubsriptions.splice(this._channelSubsriptions.indexOf(subscription.channel));
                }

            }
        }

    }

    /**
     * Close all active connections
     * @param {Boolean} unsubscribeAll Indicates whether to unsubscribe channels all before closing connection
     */
    close(unsubscribeAll = false){

        if(unsubscribeAll){

            for(let i = 0, len = this.subscriptions.length ; i < len ; i++){
                const subscription = this.subscriptions;
                subscription.unsubscribe();
            }

        }

        //  clear
        this.subscriptions = [];

        //  disconnect client
        this.connection.disconnect();

        //  reset session id
        resetSessionId();

    }

}

export default RealtimeSocketClient;



// WEBPACK FOOTER //
// ./src/common/RealtimeNotificationClient.js